import { Pencil, RotateCcw, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Gravatar from 'react-gravatar'
import {
  useGetOrgUsersLimits,
  useUpdateOrgUserLimit,
  useResetOrgUserLimit,
} from '@/services/settings'
import { useGetOrgUsers } from '@/services/user-management'
import { toast } from 'react-hot-toast'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { DataTable, TableColumn } from '@/components/data-table/DataTable'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ResetOrgUserLimitFields, WebrpcError } from '@/services/inference.gen'

type UserServerLimitCardProps = {
  orgServerLimit: number
}

type UserLimitItem = {
  user_id: number
  email: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  org_per_user_server_count: number
}

type DialogState = 'none' | 'edit' | 'confirm' | 'reset' | 'add' | 'add-confirm'

// Form schema
const editLimitFormSchema = z.object({
  serverLimit: z.number().min(1, 'Server limit must be at least 1'),
})

const addLimitFormSchema = z.object({
  userId: z.string().min(1, 'Please select a user'),
  serverLimit: z.number().min(1, 'Server limit must be at least 1'),
})

type EditLimitFormValues = z.infer<typeof editLimitFormSchema>
type AddLimitFormValues = z.infer<typeof addLimitFormSchema>

export const UserServerLimitCard = ({
  orgServerLimit,
}: UserServerLimitCardProps) => {
  const [page, setPage] = useState(0)

  // Fetch server limits
  const {
    data,
    isLoading,
    isFetching,
    refetch: refetchOrgUsersLimits,
  } = useGetOrgUsersLimits(page)

  // Fetch all org users for the dropdown
  const { data: orgUsersData } = useGetOrgUsers('active', '', undefined)

  // User for row operations
  const [selectedUser, setSelectedUser] = useState<{
    id: number
    email: string
    limit: number
  } | null>(null)

  const [dialogState, setDialogState] = useState<DialogState>('none')
  const [limitValue, setLimitValue] = useState(0)

  const form = useForm<EditLimitFormValues>({
    resolver: zodResolver(editLimitFormSchema),
    defaultValues: { serverLimit: 0 },
  })

  const addLimitForm = useForm<AddLimitFormValues>({
    resolver: zodResolver(addLimitFormSchema),
    defaultValues: { userId: '', serverLimit: orgServerLimit },
  })

  const mutation = useUpdateOrgUserLimit()
  const resetMutation = useResetOrgUserLimit()

  const closeDialog = () => {
    setDialogState('none')
    setSelectedUser(null)
  }

  const onDialogChange = (open: boolean) => {
    if (!open) closeDialog()
  }

  const handleEditClick = (user: {
    id: number
    email: string
    limit: number
  }) => {
    setSelectedUser(user)
    form.setValue('serverLimit', user.limit)
    setLimitValue(user.limit)
    setDialogState('edit')
  }

  const handleEditSubmit = (values: EditLimitFormValues) => {
    setLimitValue(values.serverLimit)
    setDialogState('confirm')
  }

  const handleResetClick = (user: {
    id: number
    email: string
    limit: number
  }) => {
    setSelectedUser(user)
    setDialogState('reset')
  }

  const handleAddClick = () => {
    addLimitForm.reset({ userId: '', serverLimit: orgServerLimit })
    setDialogState('add')
  }

  const handleAddSubmit = (values: AddLimitFormValues) => {
    const userId = parseInt(values.userId)
    const selectedUserOrg = orgUsersData?.allUsers.find(
      (userOrg) => userOrg.user_id === userId
    )

    if (!selectedUserOrg || !selectedUserOrg.user?.email) {
      toast.error('User information not found')
      setDialogState('none')
      return
    }

    setSelectedUser({
      id: userId,
      email: selectedUserOrg.user.email,
      limit: values.serverLimit,
    })

    setLimitValue(values.serverLimit)
    setDialogState('add-confirm')
  }

  const handleConfirmUpdate = () => {
    if (!selectedUser) return

    mutation.mutate(
      { org_per_user_server_count: limitValue, user_id: selectedUser.id },
      {
        onSuccess: () => {
          closeDialog()
          refetchOrgUsersLimits()
        },
        onError: (error) => {
          if (error instanceof WebrpcError) {
            toast.error(JSON.stringify(error))
          } else {
            toast.error('Failed to update server limit')
          }
          closeDialog()
        },
      }
    )
  }

  const handleConfirmReset = () => {
    if (!selectedUser) return

    resetMutation.mutate(
      {
        user_id: selectedUser.id,
        fields: ResetOrgUserLimitFields.OrgPerUserServerCount,
      },
      {
        onSuccess: () => {
          closeDialog()
          refetchOrgUsersLimits()
        },
        onError: (error) => {
          if (error instanceof WebrpcError) {
            toast.error(JSON.stringify(error))
          } else {
            toast.error('Failed to reset server limit')
          }
          closeDialog()
        },
      }
    )
  }

  // Transform the API response to a more usable format, using user management data for accurate user info
  const userLimits: UserLimitItem[] = (data?.limits || [])
    .map((item) => {
      // Find the corresponding user from orgUsersData for accurate user info including avatar
      const userFromOrgData = orgUsersData?.allUsers.find(
        (user) => user.user_id === item.user?.user_id
      )

      return {
        user_id: item.user?.user_id || 0,
        email: item.user?.email || '',
        first_name:
          userFromOrgData?.profile_metadata?.first_name ||
          item.user_organization?.profile_metadata?.first_name,
        last_name:
          userFromOrgData?.profile_metadata?.last_name ||
          item.user_organization?.profile_metadata?.last_name,
        avatar_url:
          userFromOrgData?.profile_metadata?.avatar_preview_url ||
          item.user_organization?.profile_metadata?.avatar_preview_url,
        org_per_user_server_count: item.user_limit.org_per_user_server_count,
      }
    })
    .filter(
      (item) =>
        !isNaN(item.org_per_user_server_count) &&
        item.org_per_user_server_count !== 0
    )

  // Define columns for the DataTable
  const columns: TableColumn<UserLimitItem>[] = [
    {
      id: 'user',
      header: 'User',
      width: 'w-1/2',
      cell: (item) => {
        return (
          <div className="flex items-center gap-3">
            {item.avatar_url ? (
              <img
                src={item.avatar_url}
                alt={`${item.first_name || ''} ${item.last_name || ''}'s avatar`}
                className="h-10 w-10 rounded-full object-cover border border-border"
              />
            ) : (
              <Gravatar
                email={item.email || ''}
                className="h-10 w-10 rounded-full border border-border"
              />
            )}
            <div>
              <p className="font-medium">
                {item.first_name && item.last_name
                  ? `${item.first_name} ${item.last_name}`
                  : item.email || '--'}
              </p>
              <p className="text-sm text-foreground/70">{item.email}</p>
            </div>
          </div>
        )
      },
    },
    {
      id: 'limit',
      header: 'Server Limit',
      width: 'w-1/4',
      cell: (item) => (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-primary"></span>
          <span className="font-medium">
            {item.org_per_user_server_count} active servers
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-1/4',
      cell: (item) => {
        return (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                handleEditClick({
                  id: item.user_id,
                  email: item.email,
                  limit: item.org_per_user_server_count,
                })
              }
              className="text-xs"
            >
              <Pencil className="mr-1 h-3.5 w-3.5" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                handleResetClick({
                  id: item.user_id,
                  email: item.email,
                  limit: item.org_per_user_server_count,
                })
              }
              className="text-xs text-destructive hover:text-destructive"
            >
              <RotateCcw className="mr-1 h-3.5 w-3.5" />
              Reset
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex justify-end w-full items-center mb-4">
        <Button
          variant="outline"
          onClick={handleAddClick}
          size="sm"
          data-testid="add-user-server-limit-button"
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add User Limit
        </Button>
      </div>

      <DataTable
        data={userLimits}
        columns={columns}
        keyExtractor={(item) => item.user_id}
        isLoading={isLoading}
        isFetchingNextPage={isFetching && page > 0}
        hasNextPage={data?.has_more || false}
        fetchNextPage={() => setPage((p) => p + 1)}
        emptyStateMessage="No users with custom limits found"
      />

      <Dialog open={dialogState === 'edit'} onOpenChange={onDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User Server Limit</DialogTitle>
            <DialogDescription>
              Set a custom server limit for this user.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleEditSubmit)}
              className="space-y-4 py-4"
            >
              <FormField
                control={form.control}
                name="serverLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value === 0 ? '' : field.value}
                        onChange={(e) => {
                          const value =
                            e.target.value === '' ? 0 : Number(e.target.value)
                          field.onChange(value)
                        }}
                        placeholder="Enter server limit"
                        className="w-full"
                        data-testid="server-limit-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="mt-4">
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeDialog}
                  data-testid="cancel-user-limit-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !form.formState.isValid || form.getValues('serverLimit') < 1
                  }
                  data-testid="continue-user-limit-button"
                >
                  Continue
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Add User Limit Dialog */}
      <Dialog open={dialogState === 'add'} onOpenChange={onDialogChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add User Server Limit</DialogTitle>
            <DialogDescription>
              Set a custom server limit for a user.
            </DialogDescription>
          </DialogHeader>

          <Form {...addLimitForm}>
            <form
              onSubmit={addLimitForm.handleSubmit(handleAddSubmit)}
              className="space-y-4 py-4"
            >
              <FormField
                control={addLimitForm.control}
                name="userId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select User</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="user-select-trigger">
                          <SelectValue placeholder="Select a user" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {orgUsersData?.allUsers?.map((userOrg) => {
                          // Check if this user already has a custom limit by ID
                          const hasCustomLimit = userLimits.some(
                            (item) => item.user_id === userOrg.user_id
                          )

                          return (
                            <SelectItem
                              key={userOrg.user_id}
                              value={userOrg.user_id.toString()}
                              disabled={hasCustomLimit}
                              data-testid={`user-option-${userOrg.user?.email}`}
                            >
                              {userOrg.user?.email || ''}
                              {hasCustomLimit && ' (already has custom limit)'}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={addLimitForm.control}
                name="serverLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Server Limit</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value === 0 ? '' : field.value}
                        onChange={(e) => {
                          const value =
                            e.target.value === '' ? 0 : Number(e.target.value)
                          field.onChange(value)
                        }}
                        placeholder="Enter server limit"
                        className="w-full"
                        data-testid="server-limit-input"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="mt-4">
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeDialog}
                  data-testid="cancel-user-limit-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !addLimitForm.formState.isValid ||
                    addLimitForm.getValues('userId') === '' ||
                    addLimitForm.getValues('serverLimit') < 0
                  }
                  data-testid="continue-user-limit-button"
                >
                  Continue
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Confirm Update Dialog */}
      <ConfirmationDialog
        open={dialogState === 'confirm' || dialogState === 'add-confirm'}
        onOpenChange={onDialogChange}
        title="Confirm User Server Limit Override"
        description={
          selectedUser
            ? `You're about to set a ${limitValue} active server limit for ${selectedUser.email}.`
            : ''
        }
        onConfirm={handleConfirmUpdate}
        onCancel={closeDialog}
        buttons={{
          confirm: {
            label: mutation.isPending ? 'Applying...' : 'Apply Override',
            isLoading: mutation.isPending,
          },
          cancel: { label: 'Cancel' },
        }}
        confirmation={{
          text: `${limitValue}`,
          prompt: `Please type "${limitValue}" to confirm:`,
        }}
        info={{
          warningPoints: [
            "This user's server limit will be set independently from the organization-wide limit",
            'This limit will apply to future resources. Existing resources will continue running based on the limits from when they were created.',
          ],
        }}
      />

      {/* Confirm Reset Dialog */}
      <ConfirmationDialog
        open={dialogState === 'reset'}
        onOpenChange={onDialogChange}
        title="Reset User Server Limit to Default"
        description={
          selectedUser
            ? `You're about to reset ${selectedUser.email}'s server limit to the organization default (${orgServerLimit} active servers).`
            : ''
        }
        onConfirm={handleConfirmReset}
        onCancel={closeDialog}
        buttons={{
          confirm: {
            label: resetMutation.isPending
              ? 'Resetting...'
              : 'Reset to Default',
            isLoading: resetMutation.isPending,
            isDestructive: true,
          },
          cancel: { label: 'Cancel' },
        }}
        info={{
          alert: {
            title: 'Warning',
            description:
              'This will remove any custom server limit for this user. They will follow the organization-wide default limit.',
          },
        }}
      />
    </div>
  )
}
